import { useTranslation } from "react-i18next";
import { Panel } from 'primereact/panel';
import { Chip } from "primereact/chip";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// ─── Reusable sub-section header ───────────────────────────────────────────────
function SectionHeader({ icon, label }: { icon: string; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '20px 0 10px',
        paddingBottom: '8px',
        borderBottom: '1px solid #E5E7EB',
      }}
    >
      <i className={icon} style={{ color: '#2563EB', fontSize: '14px' }} />
      <span style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 'small' }}>{label}</span>
    </div>
  );
}

// ─── Reusable styled table ──────────────────────────────────────────────────────
function ModernTable({ cols, rows }: { cols: { field: string; label: string }[]; rows: any[] }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #F3F4F6' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'small' }}>
        <thead>
          <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
            {cols.map(c => (
              <th
                key={c.field}
                style={{
                  padding: '10px 14px',
                  fontWeight: 700,
                  color: '#6B7280',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => (
            <tr
              key={idx}
              style={{ borderBottom: idx < rows.length - 1 ? '1px solid #F3F4F6' : 'none' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EFF6FF')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {cols.map(c => (
                <td key={c.field} style={{ padding: '10px 14px', color: '#374151' }}>
                  {row[c.field] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CompletaPJ({
  completaPJ,
  isConsultaNova,
}: {
  completaPJ: any;
  isConsultaNova?: boolean;
}) {
  const { t } = useTranslation();

  const stringBodyTemplate = (item: string) => item;

  if (isConsultaNova) {
    return (
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
          padding: '24px',
        }}
      >
        {/* Card Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div
            style={{
              backgroundColor: '#EFF6FF',
              borderRadius: '10px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-id-card" style={{ color: '#2563EB', fontSize: '18px' }} />
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
            {t('completaPJ')}
          </h3>
        </div>

        {/* Identity fields */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '8px',
          }}
        >
          {[
            { label: t('razaoSocial'), value: completaPJ.razaoSocial },
            { label: t('nomeFantasia'), value: completaPJ.nomeFantasia || 'Não Informado' },
            { label: t('documento'), value: completaPJ.cnpj },
            { label: t('dataAbertura'), value: completaPJ.dataAbertura },
            { label: t('situacao'), value: completaPJ.situacaoCadastral },
            { label: t('cnae'), value: `${completaPJ.cnaePrincipal} - ${completaPJ.descricaoCnaePrincipal}` },
            { label: t('naturezaJuridica'), value: `${completaPJ.naturezaJuridica} - ${completaPJ.descricaoNaturezaJuridica}` },
            { label: t('capitalSocial'), value: completaPJ.capitalSocial || 'Não Informado' },
          ].map(f => (
            <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                {f.label}
              </span>
              <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500, wordBreak: 'break-word' }}>
                {f.value}
              </span>
            </div>
          ))}
        </div>

        {/* Endereços */}
        {completaPJ.enderecos && (
          <>
            <SectionHeader icon="pi pi-map-marker" label={t('enderecos')} />
            <ModernTable
              cols={[
                { field: 'logradouro', label: t('logradouro') },
                { field: 'cep', label: t('cep') },
                { field: 'bairro', label: t('bairro') },
                { field: 'cidade', label: t('cidade') },
                { field: 'uf', label: t('uf') },
                { field: 'complemento', label: t('complemento') },
                { field: 'numero', label: t('numero') },
              ]}
              rows={completaPJ.enderecos}
            />
          </>
        )}

        {/* Sócios */}
        {completaPJ.socios && (
          <>
            <SectionHeader icon="pi pi-users" label={t('socios')} />
            <ModernTable
              cols={[
                { field: 'documento', label: t('documento') },
                { field: 'nome', label: t('nome') },
                { field: 'cargo', label: t('cargo') },
                { field: 'participacao', label: t('participacao') },
                { field: 'dataEntrada', label: t('dataEntrada') },
              ]}
              rows={completaPJ.socios}
            />
          </>
        )}

        {/* CNAEs */}
        {completaPJ.cnaes && (
          <>
            <SectionHeader icon="pi pi-tag" label={t('cnaes')} />
            <ModernTable
              cols={[
                { field: 'codigo', label: t('codigo') },
                { field: 'descricao', label: t('descricao') },
              ]}
              rows={completaPJ.cnaes}
            />
          </>
        )}

        {/* Telefones */}
        {completaPJ.telefones && (
          <>
            <SectionHeader icon="pi pi-phone" label={t('telefones')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {completaPJ.telefones.map((t2: any, idx: number) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: '#EFF6FF',
                    color: '#1E40AF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '20px',
                    padding: '4px 14px',
                    fontSize: 'small',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-phone" style={{ fontSize: '11px', marginRight: '6px' }} />
                  {t2.numero}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Emails */}
        {completaPJ.emails && (
          <>
            <SectionHeader icon="pi pi-envelope" label={t('emails')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {completaPJ.emails.map((e: any, idx: number) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: '#F0FDF4',
                    color: '#15803D',
                    border: '1px solid #BBF7D0',
                    borderRadius: '20px',
                    padding: '4px 14px',
                    fontSize: 'small',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-envelope" style={{ fontSize: '11px', marginRight: '6px' }} />
                  {e.numero}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Legacy layout
  return (
    <Panel header={t('completaPJ')}>
      <div className="grid">
        <div className="col p-fluid flex flex-wrap">
          <div>
            <label className="label-dados">{t('razaoSocial')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.razaoSocial} />
          </div>
        </div>
        <div className="col">
          <div>
            <label className="label-dados">{t('nomeFantasia')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.nomeFantasia ? completaPJ.nomeFantasia : 'Não Informado'} />
          </div>
        </div>
      </div>
      <div className="grid">
        <div className="col p-fluid flex flex-wrap">
          <div>
            <label className="label-dados">{t('documento')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.cnpj} />
          </div>
        </div>
        <div className="col">
          <div>
            <label className="label-dados">{t('dataAbertura')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.dataAbertura} />
          </div>
        </div>
      </div>
      <div className="grid">
        <div className="col p-fluid flex flex-wrap">
          <div>
            <label className="label-dados">{t('situacao')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.situacaoCadastral} />
          </div>
        </div>
        <div className="col">
          <div>
            <label className="label-dados">{t('cnae')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.cnaePrincipal + ' - ' + completaPJ.descricaoCnaePrincipal} />
          </div>
        </div>
      </div>
      <div className="grid">
        <div className="col p-fluid flex flex-wrap">
          <div>
            <label className="label-dados">{t('naturezaJuridica')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.naturezaJuridica + ' - ' + completaPJ.descricaoNaturezaJuridica} />
          </div>
        </div>
        <div className="col">
          <div>
            <label className="label-dados">{t('capitalSocial')}</label>
            <Chip style={{ width: "100%" }} label={completaPJ.capitalSocial ? completaPJ.capitalSocial : 'Não Informado'} />
          </div>
        </div>
      </div>

      {completaPJ.enderecos ? (
        <>
          <br></br>
          <Panel header={t('enderecos')}>
            <DataTable size="small" value={completaPJ.enderecos} tableStyle={{ minWidth: '50rem' }}>
              <Column field="logradouro" header={t('logradouro')}></Column>
              <Column field="cep" header={t('cep')}></Column>
              <Column field="bairro" header={t('bairro')}></Column>
              <Column field="cidade" header={t('cidade')}></Column>
              <Column field="uf" header={t('uf')}></Column>
              <Column field="complemento" header={t('complemento')}></Column>
              <Column field="numero" header={t('numero')}></Column>
            </DataTable>
          </Panel>
        </>
      ) : ''}

      {completaPJ.socios ? (
        <>
          <br></br>
          <Panel header={t('socios')}>
            <DataTable size="small" value={completaPJ.socios} tableStyle={{ minWidth: '50rem' }}>
              <Column field="documento" header={t('documento')}></Column>
              <Column field="nome" header={t('nome')}></Column>
              <Column field="cargo" header={t('cargo')}></Column>
              <Column field="participacao" header={t('participacao')}></Column>
              <Column field="dataEntrada" header={t('dataEntrada')}></Column>
            </DataTable>
          </Panel>
        </>
      ) : ''}

      {completaPJ.cnaes ? (
        <>
          <br></br>
          <Panel header={t('cnaes')}>
            <DataTable size="small" value={completaPJ.cnaes} tableStyle={{ minWidth: '50rem' }}>
              <Column field="codigo" header={t('codigo')}></Column>
              <Column field="descricao" header={t('descricao')}></Column>
            </DataTable>
          </Panel>
        </>
      ) : ''}

      {completaPJ.telefones ? (
        <>
          <br />
          <Panel header={t('telefones')}>
            <DataTable size="small" value={completaPJ.telefones} tableStyle={{ minWidth: '50rem' }}>
              <Column field="numero" body={stringBodyTemplate} header={t('fone')}></Column>
            </DataTable>
          </Panel>
        </>
      ) : ''}

      {completaPJ.emails ? (
        <>
          <br />
          <Panel header={t('emails')}>
            <DataTable size="small" value={completaPJ.emails} tableStyle={{ minWidth: '50rem' }}>
              <Column field="numero" body={stringBodyTemplate} header={t('email')}></Column>
            </DataTable>
          </Panel>
        </>
      ) : ''}
    </Panel>
  );
}
